import { UseFormReturnType } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { notify } from "shared/utils/notify";

type UseDirtyFormBlockerReturn = {
    bypass: (fn: () => unknown) => void;
};

export function useDirtyFormBlocker<S extends object>(form: UseFormReturnType<S>): UseDirtyFormBlockerReturn {
    const isDirty = form.isDirty();
    const bypassed = useRef(false);

    const blocker = useBlocker({
        shouldBlockFn: () => !bypassed.current && form.isDirty(),
        withResolver: true,
        disabled: !isDirty,
        enableBeforeUnload: isDirty,
    });

    useEffect(() => {
        window.interop?.onFormDirtyStateChange?.(isDirty);
    }, [isDirty]);

    useEffect(() => {
        if (blocker.status !== "blocked") {
            return;
        }

        modals.openConfirmModal({
            title: "Discard Changes?",
            children: "You have unsaved changes. What would you like to do?",
            labels: {
                confirm: "Discard",
                cancel: "Keep Editing",
            },
            closeOnClickOutside: false,
            onConfirm: blocker.proceed,
            onCancel: blocker.reset,
            onClose: blocker.reset,
        });
    }, [blocker.status]);

    return {
        bypass(fn) {
            bypassed.current = true;
            setTimeout(() => {
                void Promise.resolve(fn())
                    .catch(notify.catch)
                    .finally(() => {
                        bypassed.current = false;
                    });
            }, 0);
        },
    };
}
